/**
 * Validation Slice - Workflow validation state
 * Validates workflow structure and data integrity
 */

import type { StateCreator } from 'zustand';
import type { WorkflowStore, ValidationSlice, ValidationError, WorkflowNode } from './types';

export const createValidationSlice: StateCreator<WorkflowStore, [], [], ValidationSlice> = (set, get) => ({
  // Initial State
  validationErrors: [],
  isValid: true,

  // Actions
  setValidationErrors: (errors) => {
    set({
      validationErrors: errors,
      isValid: errors.filter((e) => e.type === 'error').length === 0
    });
  },

  addValidationError: (error) => {
    set((state) => {
      const newErrors = [...state.validationErrors, error];
      return {
        validationErrors: newErrors,
        isValid: newErrors.filter((e) => e.type === 'error').length === 0
      };
    });
  },

  clearValidationErrors: () => {
    set({
      validationErrors: [],
      isValid: true
    });
  },

  validateWorkflow: () => {
    const errors: ValidationError[] = [];
    const { nodes, edges } = get();

    // Check for start node
    const startNodes = nodes.filter((n) => n.data.type === 'start');
    if (startNodes.length === 0) {
      errors.push({
        id: 'no-start-node',
        type: 'error',
        message: 'Workflow must have a Start node',
        code: 'NO_START_NODE'
      });
    } else if (startNodes.length > 1) {
      errors.push({
        id: 'multiple-start-nodes',
        type: 'error',
        message: 'Workflow can only have one Start node',
        code: 'MULTIPLE_START_NODES'
      });
    }

    // Check for end node
    const endNodes = nodes.filter((n) => n.data.type === 'end');
    if (endNodes.length === 0) {
      errors.push({
        id: 'no-end-node',
        type: 'error',
        message: 'Workflow must have at least one End node',
        code: 'NO_END_NODE'
      });
    }

    // Check for disconnected nodes
    const connectedNodeIds = new Set<string>();
    edges.forEach((edge) => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });

    nodes.forEach((node) => {
      // Skip validation for single-node workflows
      if (nodes.length > 1 && !connectedNodeIds.has(node.id)) {
        errors.push({
          id: `disconnected-${node.id}`,
          nodeId: node.id,
          type: 'warning',
          message: `Node "${node.data.label}" is not connected to any other node`,
          code: 'DISCONNECTED_NODE'
        });
      }
    });

    // Check for unconfigured agent nodes
    nodes.forEach((node) => {
      if (node.data.type === 'agent' && !node.data.isConfigured) {
        errors.push({
          id: `unconfigured-${node.id}`,
          nodeId: node.id,
          type: 'warning',
          message: `Agent "${node.data.label}" has not been configured`,
          code: 'UNCONFIGURED_AGENT'
        });
      }
    });

    // Check for cycles (DAG validation)
    if (hasCycle(nodes, edges)) {
      errors.push({
        id: 'cycle-detected',
        type: 'error',
        message: 'Workflow contains a cycle. Workflows must be acyclic (DAG)',
        code: 'CYCLE_DETECTED'
      });
    }

    // Check for unreachable nodes from start
    if (startNodes.length === 1) {
      const reachableNodes = getReachableNodes(startNodes[0].id, edges);
      nodes.forEach((node) => {
        if (node.data.type !== 'start' && !reachableNodes.has(node.id)) {
          errors.push({
            id: `unreachable-${node.id}`,
            nodeId: node.id,
            type: 'warning',
            message: `Node "${node.data.label}" is not reachable from the Start node`,
            code: 'UNREACHABLE_NODE'
          });
        }
      });
    }

    set({
      validationErrors: errors,
      isValid: errors.filter((e) => e.type === 'error').length === 0
    });

    return errors.filter((e) => e.type === 'error').length === 0;
  }
});

/**
 * Helper: Check if the graph has a cycle using DFS
 */
function hasCycle(nodes: WorkflowNode[], edges: { source: string; target: string }[]): boolean {
  const adjacencyList = new Map<string, string[]>();

  // Build adjacency list
  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
  });
  edges.forEach((edge) => {
    const neighbors = adjacencyList.get(edge.source);
    if (neighbors) {
      neighbors.push(edge.target);
    }
  });

  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  function dfs(nodeId: string): boolean {
    visited.add(nodeId);
    recursionStack.add(nodeId);

    const neighbors = adjacencyList.get(nodeId) || [];
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        if (dfs(neighbor)) return true;
      } else if (recursionStack.has(neighbor)) {
        return true; // Found a cycle
      }
    }

    recursionStack.delete(nodeId);
    return false;
  }

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      if (dfs(node.id)) return true;
    }
  }

  return false;
}

/**
 * Helper: Get all nodes reachable from a starting node
 */
function getReachableNodes(startNodeId: string, edges: { source: string; target: string }[]): Set<string> {
  const reachable = new Set<string>();
  const queue = [startNodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);

    edges
      .filter((e) => e.source === current)
      .forEach((e) => {
        if (!reachable.has(e.target)) {
          queue.push(e.target);
        }
      });
  }

  return reachable;
}
